import React, { useCallback, useEffect, useRef, useState } from "react";
import "./style.css";
import {
  Button,
  ConfigProvider,
  Input,
  Modal,
  Popconfirm,
  Select,
  message,
} from "antd";
import { Box } from "@mui/material";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import { QRCodeCanvas } from "qrcode.react";
import theme from "../../theme";
import { cardIconSx } from "../../theme/globalStyle/globalStyle";
import acompanhamentoApi from "../../service/modules/acompanhamento.api";

/** As validades que a tela antiga oferecia, com o mesmo padrão de 72 h. */
const VALIDADES = [
  { value: 24, label: "24 horas" },
  { value: 72, label: "72 horas" },
  { value: 168, label: "7 dias" },
];

const VALIDADE_PADRAO = 72;

/** O mesmo azul escuro de hover que as telas refeitas do painel usam no botão principal. */
const PRIMARIA_HOVER = "#001d35";

/**
 * O antd deste bloco na paleta do painel (`src/theme`): o azul padrão dele destoava do resto do
 * sistema. É o mesmo recurso que a tela de permissões usa nos switches.
 */
const TEMA_DO_ANTD = {
  token: {
    colorPrimary: theme.colors.primary,
    colorPrimaryHover: PRIMARIA_HOVER,
    colorPrimaryActive: PRIMARIA_HOVER,
    colorLink: theme.colors.primary,
    colorError: theme.colors.highlight,
    colorText: theme.colors.textPrimary,
    fontFamily: theme.typography.fontFamily,
    borderRadius: 10,
    controlHeight: 40,
  },
  components: {
    Button: {
      fontWeight: theme.typography.weightSemibold,
      primaryShadow: "none",
      defaultShadow: "none",
      dangerShadow: "none",
      defaultHoverBg: "#f2f2f2",
      defaultHoverColor: theme.colors.highlight,
      defaultHoverBorderColor: theme.colors.highlight,
    },
  },
};

/** Tokens do tema como variáveis, no mesmo formato das telas de cadastro. */
const VARIAVEIS_DO_TEMA = {
  "--link-publico-primary": theme.colors.primary,
  "--link-publico-primary-soft": theme.colors.primarySoft,
  "--link-publico-border": theme.colors.primaryBorder,
  "--link-publico-highlight": theme.colors.highlight,
  "--link-publico-highlight-soft": theme.colors.highlightSoft,
  "--link-publico-surface": theme.colors.surface,
  "--link-publico-text-primary": theme.colors.textPrimary,
  "--link-publico-text-secondary": theme.colors.textSecondary,
  "--link-publico-radius-md": `${theme.radius.medium}px`,
  "--link-publico-radius-lg": `${theme.radius.large}px`,
  "--link-publico-font-family": theme.typography.fontFamily,
  "--link-publico-motion": theme.motion.transition,
};

const ROTULO_DO_ESTADO = {
  ativo: "Ativo",
  expirado: "Expirado",
  desativado: "Desativado",
};

function dataHora(ms) {
  return ms
    ? new Date(ms).toLocaleString("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      })
    : "—";
}

/** A mesma URL que a tela antiga montava, para os links dela e os daqui abrirem na mesma página. */
function urlDoLink(token) {
  return `${window.location.origin}/track/${token}`;
}

/**
 * O link que o operador manda para o cliente acompanhar o frete sem login. Gera, desativa e mostra
 * pelo backend: antes a tela antiga gravava direto no Firestore, com o relógio do navegador.
 *
 * Nada do link vai para o console. Quem tem o link vê o frete, então ele é tratado como credencial.
 */
function LinkPublicoDoFrete({ freteId, numeroDoFrete }) {
  const [link, setLink] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [validade, setValidade] = useState(VALIDADE_PADRAO);
  const [gravando, setGravando] = useState(false);
  const [confirmandoNovo, setConfirmandoNovo] = useState(false);

  // Resposta de um frete que o operador já trocou não pinta o link no frete novo.
  const freteRef = useRef(freteId);
  useEffect(() => {
    freteRef.current = freteId;
  }, [freteId]);

  const buscar = useCallback(async () => {
    if (!freteId) return;
    try {
      const resposta = await acompanhamentoApi.buscarLinkPublico(freteId);
      if (freteRef.current !== freteId) return;
      const atual = resposta?.data?.data || null;
      setLink(atual);
      setErro(null);
      if (atual?.validadeHoras) setValidade(atual.validadeHoras);
    } catch (e) {
      if (freteRef.current !== freteId) return;
      setErro("Não foi possível carregar o link deste frete.");
      console.error("Erro ao carregar o link público:", e?.response?.status);
    } finally {
      if (freteRef.current === freteId) setCarregando(false);
    }
  }, [freteId]);

  useEffect(() => {
    setCarregando(true);
    setLink(null);
    setValidade(VALIDADE_PADRAO);
    buscar();
  }, [buscar]);

  // Com a tela aberta, o link sai daqui na hora em que para de abrir, sem consultar de tempo em tempo.
  useEffect(() => {
    if (link?.estado !== "ativo" || !link?.expiraEm) return undefined;
    const faltam = Math.max(0, link.expiraEm - Date.now()) + 1000;
    const espera = window.setTimeout(buscar, faltam);
    return () => window.clearTimeout(espera);
  }, [link, buscar]);

  const gerar = async (substituir) => {
    setGravando(true);
    try {
      const resposta = await acompanhamentoApi.gerarLinkPublico(freteId, {
        validadeHoras: validade,
        substituir,
      });
      setLink(resposta?.data?.data || null);
      setConfirmandoNovo(false);
      message.success(
        substituir
          ? "Link novo gerado. O anterior deixou de abrir."
          : "Link gerado.",
      );
    } catch (e) {
      if (e?.response?.status === 409) {
        // Outro operador gerou um link enquanto esta tela estava aberta: mostra o dele e pergunta.
        await buscar();
        setConfirmandoNovo(true);
      } else {
        message.error(
          e?.response?.data?.message || "Não foi possível gerar o link.",
        );
        console.error("Erro ao gerar o link público:", e?.response?.status);
      }
    } finally {
      setGravando(false);
    }
  };

  const desativar = async () => {
    setGravando(true);
    try {
      const resposta = await acompanhamentoApi.desativarLinkPublico(freteId);
      setLink(resposta?.data?.data || null);
      message.success("Link desativado. Quem recebeu não consegue mais abrir.");
    } catch (e) {
      message.error(
        e?.response?.data?.message || "Não foi possível desativar o link.",
      );
      console.error("Erro ao desativar o link público:", e?.response?.status);
    } finally {
      setGravando(false);
    }
  };

  const ativo = link?.estado === "ativo";
  const url = ativo ? urlDoLink(link.token) : "";

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(url);
      message.success("Link copiado.");
    } catch (e) {
      message.error("Não foi possível copiar. Selecione o link e copie.");
    }
  };

  const abrirWhatsApp = () => {
    const texto = numeroDoFrete
      ? `Acompanhe o frete ${numeroDoFrete} em tempo real: ${url}`
      : `Acompanhe a rota em tempo real: ${url}`;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(texto)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const seletorDeValidade = (
    <Select
      className="link-publico__validade"
      value={validade}
      options={VALIDADES}
      onChange={setValidade}
      disabled={gravando}
      aria-label="Validade do link"
    />
  );

  let corpo;
  if (carregando) {
    corpo = <p className="link-publico__nota">Carregando o link...</p>;
  } else if (erro) {
    corpo = <p className="link-publico__erro">{erro}</p>;
  } else if (ativo) {
    corpo = (
      <div className="link-publico__ativo">
        <div className="link-publico__dados">
          <div className="link-publico__campo">
            <Input
              readOnly
              value={url}
              onFocus={(e) => e.target.select()}
              aria-label="Link para o cliente"
            />
            <Button
              type="primary"
              icon={<ContentCopyOutlinedIcon fontSize="small" />}
              onClick={copiar}
            >
              Copiar
            </Button>
          </div>
          <div className="link-publico__acoes">
            <Button
              icon={<WhatsAppIcon fontSize="small" />}
              onClick={abrirWhatsApp}
            >
              Enviar por WhatsApp
            </Button>
            <Popconfirm
              title="Desativar o link?"
              description="Quem já recebeu deixa de conseguir abrir."
              okText="Desativar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={desativar}
            >
              <Button
                danger
                loading={gravando}
                icon={<LinkOffOutlinedIcon fontSize="small" />}
              >
                Desativar
              </Button>
            </Popconfirm>
          </div>
          <div className="link-publico__rodape">
            <span>Válido até {dataHora(link.expiraEm)}</span>
            <span className="link-publico__novo">
              {seletorDeValidade}
              <Button
                disabled={gravando}
                icon={<AutorenewOutlinedIcon fontSize="small" />}
                onClick={() => setConfirmandoNovo(true)}
              >
                Gerar novo
              </Button>
            </span>
          </div>
        </div>
        <div className="link-publico__qr">
          <QRCodeCanvas value={url} size={132} fgColor={theme.colors.primary} />
          <span>Aponte a câmera do celular</span>
        </div>
      </div>
    );
  } else {
    corpo = (
      <div className="link-publico__sem-link">
        <p className="link-publico__nota">
          {link?.estado === "expirado" &&
            `Expirou em ${dataHora(link.expiraEm)}. `}
          {link?.estado === "desativado" &&
            `Desativado${link.desativadoEm ? ` em ${dataHora(link.desativadoEm)}` : ""}. `}
          Escolha por quanto tempo o link vale e gere para mandar ao cliente.
        </p>
        <div className="link-publico__acoes">
          {seletorDeValidade}
          <Button
            type="primary"
            loading={gravando}
            icon={<ShareOutlinedIcon fontSize="small" />}
            onClick={() => gerar(false)}
          >
            {link?.estado === "sem-link" || !link
              ? "Gerar link"
              : "Gerar novo link"}
          </Button>
        </div>
      </div>
    );
  }

  const estado = !carregando && !erro ? ROTULO_DO_ESTADO[link?.estado] : null;

  return (
    <ConfigProvider theme={TEMA_DO_ANTD}>
      <section className="link-publico" style={VARIAVEIS_DO_TEMA}>
        <header className="link-publico__cabecalho">
          <Box sx={cardIconSx}>
            <ShareOutlinedIcon sx={{ color: "#ffffff" }} fontSize="small" />
          </Box>
          <div className="link-publico__textos">
            <h2 className="link-publico__titulo">
              Link para o cliente acompanhar
            </h2>
            <p className="link-publico__subtitulo">
              Quem tiver o link acompanha o frete sem login: status, pontos de
              parada e o trajeto no mapa. Motorista, placa e valores não
              aparecem.
            </p>
          </div>
          {estado && (
            <span
              className={`link-publico__estado link-publico__estado--${link.estado}`}
            >
              {estado}
            </span>
          )}
        </header>

        {corpo}

        <Modal
          title="Gerar um link novo?"
          open={confirmandoNovo}
          onCancel={() => setConfirmandoNovo(false)}
          onOk={() => gerar(true)}
          okText="Gerar novo"
          cancelText="Cancelar"
          confirmLoading={gravando}
        >
          <p className="link-publico__modal-texto">
            O link atual para de funcionar na hora, inclusive para quem já o
            recebeu. O novo vale por{" "}
            {VALIDADES.find((opcao) => opcao.value === validade)?.label}.
          </p>
        </Modal>
      </section>
    </ConfigProvider>
  );
}

export default LinkPublicoDoFrete;
